import { ApolloCache, ApolloClient } from '@apollo/client'
import { DataIdObject, dataIdFromObject } from '@sketch/graphql-cache'

import { updateCacheEntries, UpdaterFunction } from './updateCacheEntries'
import { Obj } from './traverseAndCollectRefs'

type ScopeValidator = (key: string) => boolean

export function emptyPagination(pagination: Obj, resetTotalCount = true) {
  const { entries, meta }: any = pagination || {}
  if (!Array.isArray(entries)) return

  entries.length = 0

  if (resetTotalCount && meta && typeof meta.totalCount === 'number') {
    meta.totalCount = 0
  }
}

function isPaginationObj(
  obj: Obj
): obj is { entries: unknown[]; meta?: { totalCount: number } } {
  return (
    typeof obj === 'object' && 'entries' in obj && Array.isArray(obj.entries)
  )
}

export function emptyPaginated<ParentIdentity extends DataIdObject>(
  cache: ApolloCache<any> | ApolloClient<any>,
  scopeValidator: ParentIdentity | ScopeValidator,
  metaUpdater: undefined | ((pagination: unknown) => void) = undefined
) {
  const apolloCache = 'cache' in cache ? cache.cache : cache

  const updater: UpdaterFunction = object => {
    emptyPagination(object)
    metaUpdater?.(object?.meta)
  }

  if (typeof scopeValidator === 'function') {
    return updateCacheEntries({
      cache: apolloCache,
      shouldTraverse: () => true,
      shouldCollect: ({ path, object }) =>
        isPaginationObj(object) && scopeValidator(path),
      updater,
    })
  }

  const parentCacheId = dataIdFromObject(scopeValidator)

  if (!parentCacheId) return

  return updateCacheEntries({
    cache: apolloCache,
    shouldTraverse: ({ path }) => path.includes(parentCacheId),
    shouldCollect: ({ object }) => isPaginationObj(object),
    updater,
  })
}
